import dayjs from 'dayjs';

import {LangKeyType} from '../../../../../../../../provider/locale/translation/type';
import {ProvidersIdsEnum} from '../../../../../../../../util/type';

export const abuseFormId = 'new-abuse-form';

export enum AbuseFormItemEnum {
    Choices = 'choices',
    Description = 'description',
}

export type AbuseFormType = {
    choices: Array<LangKeyType>;
    description?: string;
};

// Catalogs that reject longer complaints; keep in sync with the backend ABUSE_MAX_LENGTH_BY_CATALOG.
const abuseMaxLengthByCatalog: Partial<Record<number, number>> = {
    [ProvidersIdsEnum.doubleGis]: 450,
};

// The strictest limit among the given catalogs, or null when none of them restricts the length.
export function getAbuseMaxLength(catalogIdList: Array<number | null | undefined>): number | null {
    const limitList = catalogIdList
        .map((catalogId) => (catalogId ? abuseMaxLengthByCatalog[catalogId] ?? null : null))
        .filter((limit): limit is number => limit !== null);

    return limitList.length > 0 ? Math.min(...limitList) : null;
}

// 2GIS asks the review author to confirm the visit; 2GIS does not act on this complaint for reviews older than a year.
export const twoGisNoClientAbuseReasonId = 1;

export const abuseReasons: Array<{title: LangKeyType; id: number}> = [
    {title: 'REVIEWS__COMPLAINT_POPUP__2GIS__NO_CLIENT', id: twoGisNoClientAbuseReasonId},
    {title: 'REVIEWS__COMPLAINT_POPUP__OFFENSIVE_CONTENT', id: 2},
    {title: 'REVIEWS__COMPLAINT_POPUP__MENTION_COMPETITOR', id: 3},
    {title: 'REVIEWS__COMPLAINT_POPUP__NOT_APPLY_THIS_COMPANY', id: 4},
    {title: 'REVIEWS__COMPLAINT_POPUP__PRIVACY_VIOLATION', id: 5},
];

// Google's own "Report review" reasons (single-select). `code` is the backend category value
// sent as the abuse `reason` (see backend google_com.review_abuse.reasons.GoogleReportReason).
export const googleAbuseReasons: Array<{title: LangKeyType; id: number; code: string}> = [
    {title: 'REVIEWS__COMPLAINT_POPUP__GOOGLE__OFF_TOPIC', id: 1, code: 'off_topic'},
    {title: 'REVIEWS__COMPLAINT_POPUP__GOOGLE__SPAM', id: 2, code: 'spam'},
    {title: 'REVIEWS__COMPLAINT_POPUP__GOOGLE__CONFLICT_OF_INTEREST', id: 3, code: 'conflict_of_interest'},
    {title: 'REVIEWS__COMPLAINT_POPUP__GOOGLE__PROFANITY', id: 4, code: 'profanity'},
    {title: 'REVIEWS__COMPLAINT_POPUP__GOOGLE__HARMFUL', id: 5, code: 'harmful'},
    {title: 'REVIEWS__COMPLAINT_POPUP__GOOGLE__BULLYING', id: 6, code: 'bullying_or_harassment'},
    {title: 'REVIEWS__COMPLAINT_POPUP__GOOGLE__HATE_SPEECH', id: 7, code: 'discrimination_or_hate_speech'},
    {title: 'REVIEWS__COMPLAINT_POPUP__GOOGLE__PERSONAL_INFORMATION', id: 8, code: 'personal_information'},
    {title: 'REVIEWS__COMPLAINT_POPUP__GOOGLE__NOT_HELPFUL', id: 9, code: 'not_helpful'},
];

export function isGoogleCatalog(catalogId?: number | null): boolean {
    return (
        catalogId === ProvidersIdsEnum.google ||
        catalogId === ProvidersIdsEnum.googleMaps ||
        catalogId === ProvidersIdsEnum.googleMapsNew
    );
}

function isTwoGisCatalog(catalogId?: number | null): boolean {
    return catalogId === ProvidersIdsEnum.doubleGis;
}

const twoGisNoClientAbuseMaxReviewAgeInYears = 1;

// Strictly older than one year; a missing or unparsable date keeps the reason available.
export function isOlderThanYear(dateIso: string | null | undefined, now: Date = new Date()): boolean {
    if (!dateIso) {
        return false;
    }

    const date = dayjs(dateIso);

    return date.isValid() && date.isBefore(dayjs(now).subtract(twoGisNoClientAbuseMaxReviewAgeInYears, 'year'));
}

export function isAbuseReasonDisabled(parameters: {
    reasonId: number;
    catalogId?: number | null;
    createdInCatalog?: string | null;
    now?: Date;
}): boolean {
    return (
        parameters.reasonId === twoGisNoClientAbuseReasonId &&
        isTwoGisCatalog(parameters.catalogId) &&
        isOlderThanYear(parameters.createdInCatalog, parameters.now)
    );
}

// Drops abuse reasons that are not applicable to a specific review (e.g. the 2GIS "no client"
// reason for a review older than a year), so a bulk complaint only sends reasons that apply.
export function filterChoicesForReview(parameters: {
    choices: Array<LangKeyType>;
    catalogId?: number | null;
    createdInCatalog?: string | null;
    now?: Date;
}): Array<LangKeyType> {
    return parameters.choices.filter((choice) => {
        const reason = abuseReasons.find((item) => item.title === choice);

        if (!reason) {
            return true;
        }

        return !isAbuseReasonDisabled({
            reasonId: reason.id,
            catalogId: parameters.catalogId,
            createdInCatalog: parameters.createdInCatalog,
            now: parameters.now,
        });
    });
}

// The Report button is available for a non-deleted review when the backend allows it, or — when the
// Google Playwright complaint flow is enabled — for any Google review regardless of `ableToAbuse`.
export function isAbuseAvailable(parameters: {
    isDeleted: boolean;
    ableToAbuse: boolean;
    catalogId?: number | null;
    googlePlaywrightEnabled: boolean;
}): boolean {
    return (
        !parameters.isDeleted &&
        (parameters.ableToAbuse || (parameters.googlePlaywrightEnabled && isGoogleCatalog(parameters.catalogId)))
    );
}
